import { useState } from 'react'
import clsx from 'clsx'
import Lottie from 'lottie-react'
import LoaderAdm from '../../../../../../../assets/svgIcons/LoaderAdm.json'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined'
import { formatDate } from '../../../../../../utils/formatter'
import { getDteColor } from '../../utils/dteColors'
import { getDteAbbreviation } from '../../utils/dteLabels'
import iconEnlace from '../../../../../../../assets/iconos/editDoc.png'

export default function AdminDocsTable({
  rows,
  isLoading,
  onSelectDoc,

  // ✅ nuevos props
  getRowKey,
  detailsByKey = {},
  loadingActionByKey = {}
}) {
  const [order, setOrder] = useState({ field: 'date_issued', direction: 'desc' })

  const columns = [
    { col: 'Archivo', field: 'archivo', align: 'center', w: 'w-[110px]' },
    { col: 'Receptor', field: 'receiver_tax_id', align: 'left', w: 'w-[140px]' },
    { col: 'Razón Social', field: 'receiver_name', align: 'left', w: 'w-[340px]' },
    { col: 'Documento', field: 'dte_type_description', align: 'left', w: 'w-[220px]' },
    { col: 'Folio', field: 'folio', align: 'center', w: 'w-[90px]' },
    { col: 'Fecha', field: 'date_issued', align: 'center', w: 'w-[120px]' },
    { col: 'Monto', field: 'amount_with_iva', align: 'right', w: 'w-[140px]' },
    { col: 'Acciones', field: 'acciones', align: 'center', w: 'w-[100px]' }
    // { col: 'Estado', field: 'estado_ui', align: 'center', w: 'w-[140px]' }
  ]

  const handleSort = field => {
    if (field === 'archivo') return
    const isAsc = order.field === field && order.direction === 'asc'
    setOrder({ field, direction: isAsc ? 'desc' : 'asc' })
  }

  const rowsWithUI = (rows || []).map(r => ({
    ...r,
    estado_ui: r.is_paid ? 'PAGADA' : 'NO PAGADA',
    dte_type_description: r?.dte_type_description || r?.dte_type?.description || '-'
  }))

  const sortedRows = [...rowsWithUI].sort((a, b) => {
    const field = order.field
    const aVal = a?.[field]
    const bVal = b?.[field]

    if (field === 'amount_with_iva') {
      const na = Number(aVal ?? 0)
      const nb = Number(bVal ?? 0)
      return order.direction === 'asc' ? na - nb : nb - na
    }

    if (field === 'date_issued') {
      const da = aVal ? new Date(aVal).getTime() : 0
      const db = bVal ? new Date(bVal).getTime() : 0

      if (da === db) {
        const ta = a?.created_at ? new Date(a.created_at).getTime() : 0
        const tb = b?.created_at ? new Date(b.created_at).getTime() : 0
        return order.direction === 'asc' ? ta - tb : tb - ta
      }

      return order.direction === 'asc' ? da - db : db - da
    }

    const sa = String(aVal ?? '').toLowerCase()
    const sb = String(bVal ?? '').toLowerCase()
    if (sa > sb) return order.direction === 'asc' ? 1 : -1
    if (sa < sb) return order.direction === 'asc' ? -1 : 1
    return 0
  })

  const Spinner = () => (
    <span className='inline-block w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin' />
  )

  if (isLoading) {
    return (
      <div className='flex items-center justify-center h-64 bg-white border border-gray-200 rounded'>
        <Lottie animationData={LoaderAdm} loop style={{ width: 130, height: 130 }} />
      </div>
    )
  }

  if (!rows || rows.length === 0) {
    return (
      <div className='py-10 text-center text-xs bg-white border border-gray-200 rounded'>
        No hay datos
      </div>
    )
  }

  return (
    <div
      className='overflow-auto border max-h-[500px] border-gray-200 rounded text-xs bg-white'
      style={{ fontSize: '12px', lineHeight: 1.5 }}
    >
      <table className='w-full table-auto border-collapse'>
        <thead className='sticky top-0 bg-gray-100'>
          <tr>
            {columns.map((c, idx) => (
              <th
                key={idx}
                onClick={() => handleSort(c.field)}
                className={clsx(
                  'p-2 select-none font-semibold text-gray-600',
                  c.field !== 'archivo' ? 'cursor-pointer' : '',
                  c.align === 'left'
                    ? 'text-left'
                    : c.align === 'right'
                      ? 'text-right'
                      : 'text-center',
                  c.w
                )}
              >
                <div
                  className={clsx(
                    'flex items-center gap-1',
                    c.align === 'left'
                      ? 'justify-start'
                      : c.align === 'right'
                        ? 'justify-end'
                        : 'justify-center'
                  )}
                >
                  <span>{c.col}</span>
                  {c.field !== 'archivo' && (
                    <KeyboardArrowDownIcon
                      fontSize='small'
                      className={clsx('transition-transform', {
                        'opacity-30': order.field !== c.field,
                        'rotate-180': order.field === c.field && order.direction === 'desc'
                      })}
                    />
                  )}
                </div>
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {sortedRows.map((row, idx) => {
            const key = getRowKey?.(row)
            const detail = key ? detailsByKey?.[key] : null

            const activeAction = key ? loadingActionByKey?.[key] : null
            const isDocLoading =
              detail?.status === 'loading' || activeAction === 'pdf' || activeAction === 'nt'
            const showPdfSpinner = isDocLoading && activeAction === 'pdf'
            const showNtSpinner = isDocLoading && activeAction === 'nt'

            return (
              <tr
                key={row.id ?? idx}
                className='hover:bg-gray-50 border-b border-gray-100 last:border-0'
              >
                <td className='p-3 text-center'>
                  <button
                    type='button'
                    onClick={() => onSelectDoc?.(row)}
                    disabled={isDocLoading || row?.dte_type_code === 61}
                    className={clsx(
                      'inline-flex items-center justify-center transition-transform duration-150 ease-out hover:scale-110 active:scale-95 text-red-500 hover:text-red-700',
                      {
                        // Agregamos la condición del código 61 aquí también:
                        'opacity-40 cursor-not-allowed': isDocLoading || row.dte_type_code === 61
                      }
                    )}
                    title='Ver Documento'
                  >
                    {showPdfSpinner ? <Spinner /> : <PictureAsPdfOutlinedIcon fontSize='small' />}
                  </button>
                </td>

                <td className='p-3 text-left align-top'>
                  <span className='font-medium'>{row.receiver_tax_id || '-'}</span>
                </td>
                <td className='p-3 text-left align-top'>
                  <span className='font-bold'>{row.receiver_name || '-'}</span>
                </td>
                <td className='p-3 text-left align-top'>
                  <span
                    className={clsx(
                      'px-2 py-1 rounded-full text-[9px] font-bold border',
                      getDteColor(row?.dte_type_code ?? row?.dte_type?.code)
                    )}
                  >
                    <span className='hidden 2xl:inline'>
                      {row.dte_type_description || '-'}{row?.document_states?.[0]?.label ? ` ${row.document_states[0].label}` : ''}
                    </span>
                    <span className='inline 2xl:hidden'>
                      {getDteAbbreviation(row?.dte_type_code ?? row?.dte_type?.code)}{row?.document_states?.[0]?.label ? ` ${row.document_states[0].label}` : ''}
                    </span>
                  </span>
                </td>
                <td className='p-3 text-center'>{row.folio || '-'}</td>
                <td className='p-3 text-center'>
                  {row.date_issued ? formatDate(row.date_issued) : '-'}
                </td>
                <td className='p-3 text-right font-medium'>
                  {Number(row.amount_with_iva ?? 0).toLocaleString('es-CL', {
                    style: 'currency',
                    currency: 'CLP'
                  })}
                </td>

                <td className='p-3 text-center'>
                  <div className='flex flex-col items-center gap-1'>
                    <button
                      type='button'
                      onClick={() => onSelectDoc?.(row, 'nt')}
                      disabled={isDocLoading || row?.dte_type_code === 61}
                      className={clsx(
                        'flex items-center justify-center w-8 h-8 rounded-full shadow-sm transition-transform hover:scale-110 active:scale-95 border border-gray-400',
                        {
                          'bg-white text-slate-400': !isDocLoading && row.dte_type_code !== 61,
                          'opacity-40 cursor-not-allowed': isDocLoading || row.dte_type_code === 61
                        }
                      )}
                      title='Emitir Nota de Crédito'
                    >
                      {showNtSpinner ? (
                        <Spinner />
                      ) : (
                        <img src={iconEnlace} alt='Emitir NC' className='w-5 h-5' />
                      )}
                    </button>
                    <span className='text-[10px] text-gray-500 font-bold'>Emitir NC</span>
                  </div>
                </td>

                {/* <td className='p-3 text-center'>
                  <span
                    className={clsx('font-bold', {
                      'text-green-600': row.is_paid,
                      'text-yellow-600': !row.is_paid
                    })}
                  >
                    {row.is_paid ? 'PAGADA' : 'NO PAGADA'}
                  </span>
                </td> */}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
